import { create } from 'zustand';
import { api } from '../services/api';

interface SettingsState {
  timeZone: string;
  saveTimeZone: (timeZone: string) => Promise<void>;
  aiProvider: string;
  aiApiKey: string;
  aiModel: string;
  searchProvider: string;
  aiProviders: string[];
  searchProviders: string[];
  isLoading: boolean;
  error: string | null;
  fetchSettings: () => Promise<void>;
  fetchProviders: () => Promise<void>;
  saveAIProvider: (provider: string, apiKey?: string, model?: string) => Promise<void>;
  saveSearchProvider: (provider: string) => Promise<void>;
  setAuthToken: (token: string) => Promise<void>;
  setAIProvider: (provider: string) => void;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  saveTimeZone: async timeZone => { await api.post('/api/settings/timezone', { timeZone }); set({ timeZone }); },
  aiProvider: 'mock',
  aiApiKey: '',
  aiModel: '',
  searchProvider: 'duckduckgo-html',
  aiProviders: ['mock', 'openrouter', 'cloudflare-ai'],
  searchProviders: ['duckduckgo-html'],
  isLoading: false,
  error: null,

  fetchSettings: async () => {
    const token = localStorage.getItem('auth-token');
    if (!token) {
      set({ isLoading: false });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const settings = await api.get<Record<string, string>>('/api/settings');
      set({
        timeZone: settings.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        aiProvider: settings.ai_provider || 'mock',
        aiApiKey: settings.ai_api_key || '',
        aiModel: settings.ai_model || '',
        searchProvider: settings.search_provider || 'duckduckgo-html',
        isLoading: false,
      });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  fetchProviders: async () => {
    try {
      const { ai, search } = await api.get<{ ai: string[]; search: string[] }>('/api/settings/providers');
      set({ aiProviders: ai, searchProviders: search });
    } catch (e) {
      console.warn('Failed to fetch providers:', e);
    }
  },

  saveAIProvider: async (provider, apiKey, model) => {
    await api.post('/api/settings/ai', { provider, apiKey, model });
    set({ aiProvider: provider, aiApiKey: apiKey || '', aiModel: model || '' });
  },

  saveSearchProvider: async (provider) => {
    await api.post('/api/settings/search', { provider });
    set({ searchProvider: provider });
  },

  setAuthToken: async (token) => {
    await api.post('/api/settings/auth/token', { token });
  },

  setAIProvider: (provider) => set({ aiProvider: provider, aiModel: '', aiApiKey: '' }),
}));
